// Which credentials an agent went after, and whether it got them.
//
//   aws secretsmanager get-secret-value --secret-id origin/orchestrator-env --query SecretString
//   aws secretsmanager list-secrets --profile … --query 'SecretList[].Name'
//   security find-generic-password -s forgejo-claude -w
//
// The target is the SECRET's name — a durable identifier in someone else's
// namespace, which is the strongest kind this pipeline has found. What makes the
// lens worth having is the OUTCOME, and one of its states exists nowhere else in
// the trace: a credential read the HARNESS refused. That refusal arrives as a tool
// error, not as shell output, so a lens reading only stdout reports the attempt as
// having produced nothing and loses the fact that something stopped it.
//
// It shows what came back, like every other lens here. An earlier version redacted
// the command and dropped the result on a rule I invented — nobody asked for it, and
// hiding what a tenant's own operator is entitled to see is not this lens's call to
// make. `valueReachedTrace` still flags when credential material is present, because
// that is a useful fact, not because the material needs concealing.

import { flagOf, positionalsOf, shellStatements } from "../../src/mine/statements.js";

const VALUE_FLAGS = new Set(["--secret-id", "--name", "--profile", "--region", "--query", "--output",
  "-s", "-a", "-l", "-w", "--service", "--account", "--version-id", "--version-stage", "--field"]);

// `kv` is Vault's SUBCOMMAND NAMESPACE, not a verb. Having it here matched before the
// real verb in `vault kv put` and `vault kv delete`, so a credential WRITE was
// reported as a read — in a lens whose whole purpose is auditing credential access.
const READ_VERBS = /^(?:get-secret-value|get-parameter|get-parameters|get-parameters-by-path|read|get)$/i;
// Namespaces to step over before looking for the verb.
const NAMESPACE_RE = /^(?:kv|secret|secretsmanager|ssm|secrets)$/i;
const LIST_VERBS = /^(?:list-secrets|list-parameters|describe-secret|describe-parameters|list)$/i;
const WRITE_VERBS = /^(?:create-secret|put-secret-value|update-secret|put-parameter|delete-secret|put|write|delete)$/i;

/** Which store, and how it was addressed. */
export function storeOf(st) {
  const p = st.program.toLowerCase();
  if (p === "aws") {
    const svc = positionalsOf(st.args, VALUE_FLAGS)[0] || "";
    if (/^secretsmanager$/i.test(svc)) return "aws-secrets-manager";
    if (/^ssm$/i.test(svc)) return "aws-parameter-store";
    return null;
  }
  if (p === "security") return "macos-keychain";
  if (p === "vault") return "vault";
  if (/^(?:gcloud|az)$/.test(p) && /secrets?/i.test(st.text)) return p === "gcloud" ? "gcp-secret-manager" : "azure-key-vault";
  return null;
}

export function verbAndKindOf(st) {
  const bare = positionalsOf(st.args, VALUE_FLAGS).filter((t) => !NAMESPACE_RE.test(t));
  // Write and list are checked BEFORE read, so an ambiguous token can never
  // downgrade a mutation to a read. Getting this order wrong understates what an
  // agent did, which is the dangerous direction for this lens.
  const verb = bare.find((t) => WRITE_VERBS.test(t) || LIST_VERBS.test(t))
    || bare.find((t) => READ_VERBS.test(t))
    || bare.find((t) => /^find-(?:generic|internet)-password$/i.test(t))
    || null;
  if (!verb) return { verb: null, kind: null };
  if (/^find-/i.test(verb)) return { verb, kind: "read" };
  if (LIST_VERBS.test(verb)) return { verb, kind: "enumerate" };
  if (WRITE_VERBS.test(verb)) return { verb, kind: "write" };
  return { verb, kind: "read" };
}

/** The secret's name. `null` for an enumeration, which by definition names none. */
export function secretNameOf(st, kind) {
  if (kind === "enumerate") return null;
  const named = flagOf(st.args, "--secret-id", "--name", "-s", "--service", "--path");
  // A secret addressed through a variable set in an EARLIER CALL cannot be
  // resolved, but the variable name is itself stable and meaningful: an agent
  // referring to $DB_SECRET_ARN forty-nine times is referring to one secret.
  // Falling through to a per-op key instead rendered 53 singleton groups all
  // called "an unnamed secret", which is worse than naming it by reference.
  if (named && /^\$\{?\w+\}?$/.test(named)) return named.replace(/[${}]/g, "").replace(/^/, "$");
  if (named && !named.startsWith("-")) return named;
  // `vault kv read secret/foo` and `security find-generic-password -s x` are the
  // two positional forms that occur.
  // The VERB is not the secret. `get-secret-value` and `find-generic-password`
  // both contain dashes and were being reported as secret names, so half the
  // catalog was a group called after the command that read it.
  const bare = positionalsOf(st.args, VALUE_FLAGS);
  const pathish = bare.find((t) => /[/_-]/.test(t)
    && !READ_VERBS.test(t) && !LIST_VERBS.test(t) && !WRITE_VERBS.test(t)
    && !/^(?:secretsmanager|ssm|kv|find-generic-password|find-internet-password)$/i.test(t)
    && !/^-/.test(t));
  return pathish || null;
}

// The harness refusal, which is a tool error rather than shell output.
const HARNESS_DENIED_RE = /denied by the .*classifier|Permission for this action was denied|requires approval|user (?:rejected|doesn't want)/i;
const PROVIDER_DENIED_RE = /\bAccessDenied|\bUnauthorized|\bnot authorized to perform|ExpiredToken|InvalidClientTokenId|\bcould not be found in the keychain/i;
// The shapes a secret's value takes when it lands in a result.
const VALUE_PRESENT_RE = /-----BEGIN [A-Z ]*PRIVATE KEY-----|"SecretString"|\b[A-Za-z0-9_-]{32,}\b/;

export function extractSecretOps(turns) {
  const ops = [];
  for (const st of shellStatements(turns)) {
    const store = storeOf(st);
    if (!store) continue;
    const { verb, kind } = verbAndKindOf(st);
    if (!kind) continue;

    const out = st.output || "";
    const notice = st.notice || "";
    const outcome = HARNESS_DENIED_RE.test(notice) || HARNESS_DENIED_RE.test(out) ? "denied-by-harness"
      : PROVIDER_DENIED_RE.test(out) ? "refused-by-provider"
      : st.outputStatus === "observed" && out ? "granted"
      : "unrecorded";

    // Asked and answered is not the same as the answer landing in the transcript.
    const valueReachedTrace = outcome === "granted" && kind === "read" && VALUE_PRESENT_RE.test(out);

    ops.push({
      key: st.key,
      store,
      verb: verb ? verb.toLowerCase() : null,
      kind,
      secret: secretNameOf(st, kind),
      // Named by reference, so the identity is the agent's own variable rather
      // than the store's key.
      byReference: /^\$/.test(secretNameOf(st, kind) || ""),
      profile: flagOf(st.args, "--profile"),
      region: flagOf(st.args, "--region"),
      outcome,
      valueReachedTrace,
      output: out ? (out.length > 4000 ? out.slice(0, 4000) : out) : null,
      outputStatus: st.outputStatus,
      ok: st.ok,
      steps: [{ kind: kind === "enumerate" ? "listed" : kind, cmd: st.text.slice(0, 260) }],
      checks: [{
        kind: outcome === "denied-by-harness" ? "blocked" : kind,
        tool: secretNameOf(st, kind) || `all secrets in ${store}`,
        // The finding leads. Which store it was is context; whether a credential's
        // value entered the transcript is the fact a reader needs off one row.
        scope: [
          valueReachedTrace ? "value entered the trace" : null,
          store,
          flagOf(st.args, "--region"),
        ].filter(Boolean),
        outcome,
      }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  if (op.outcome === "denied-by-harness") {
    return "the harness refused this credential read before it ran — the agent asked and was stopped, which is a different fact from asking and failing";
  }
  if (op.outcome === "refused-by-provider") return "the credential store refused the call — the agent has no secret from it";
  if (op.outcome === "granted" && !op.valueReachedTrace) return "the call succeeded and no credential material appears in the result";
  if (op.outcome === "granted") return "the credential's value entered this trace";
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — the result never entered this trace";
    case "none": return "ran and captured no output";
    default: return null;
  }
}
